import { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";
import * as amplitude from "@amplitude/analytics-browser";
import { Identify } from "@amplitude/analytics-browser";
import type { CheckoutPlan } from "./LandingPage";
import { getInternalCoreEndpoint } from "./utils";

const STORAGE_KEY = "bws_referral";
const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
const CODE_PATTERN = /^[A-Z0-9]+(?:-[A-Z0-9]+)*$/;
// Member links carry `?referral=CODE`. Other sites send `?ref=` with their own
// name or an affiliate code, so `?ref=` counts only in the shape core generates
// (older member links use it).
const LEGACY_REF_PATTERN = /^(?:[A-Z]{1,10}-[2-9A-HJ-NP-Z]{4}|[2-9A-HJ-NP-Z]{8})$/;

/** The code of a referral link ("" if malformed), or null if `search` is not a referral link. */
function getReferralLinkCode(search: string): string | null {
  const params = new URLSearchParams(search);
  if (params.has("referral")) {
    const values = params.getAll("referral");
    return values.length === 1 ? values[0].trim().toUpperCase() : "";
  }
  const legacy = params.getAll("ref");
  const code = legacy.length === 1 ? legacy[0].trim().toUpperCase() : "";
  return LEGACY_REF_PATTERN.test(code) ? code : null;
}

export const isReferralLink = (search: string) => getReferralLinkCode(search) !== null;

export type Referral = {
  code: string;
  referrerFirstName: string;
  savedAt: number;
};

function clearReferral() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* Storage can be disabled. */ }
}

export function getStoredReferral(): Referral | null {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (value && typeof value.code === "string" && value.code.length <= 80 &&
        CODE_PATTERN.test(value.code) && typeof value.referrerFirstName === "string" &&
        value.referrerFirstName.trim() && typeof value.savedAt === "number" &&
        Number.isFinite(value.savedAt) && value.savedAt <= Date.now() &&
        Date.now() - value.savedAt < MAX_AGE) {
      return value;
    }
  } catch { /* Invalid or inaccessible storage fails closed. */ }
  clearReferral();
  return null;
}

// These plan IDs identify the annual products; isMonthly describes displayed
// pricing and is true even for the annual plan. Core still enforces eligibility.
export function isReferralAnnualPlan(plan?: CheckoutPlan): boolean {
  return plan?.id === "committed" && !plan.isOneTime;
}

export function getCheckoutReferralCode(plan?: CheckoutPlan, has14DayTrial = true): string | undefined {
  return has14DayTrial && isReferralAnnualPlan(plan) && !!plan?.trialProductId
    ? getStoredReferral()?.code
    : undefined;
}

let landingLookup: Promise<Referral | null> | undefined;

function validateLanding(): Promise<Referral | null> {
  // One request per document landing, including StrictMode's second mount.
  if (landingLookup) return landingLookup;
  const code = getReferralLinkCode(window.location.search);
  if (code === null) {
    return (landingLookup = Promise.resolve(getStoredReferral()));
  }

  clearReferral();
  if (code.length > 80 || !CODE_PATTERN.test(code)) {
    return (landingLookup = Promise.resolve(null));
  }

  landingLookup = (async () => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(
        `${getInternalCoreEndpoint()}/internal/public/referral/code/${encodeURIComponent(code)}`,
        { signal: controller.signal }
      );
      if (!response.ok) return null;
      const { data } = await response.json();
      if (!data || data.code?.toUpperCase() !== code ||
          typeof data.referrerFirstName !== "string" || !data.referrerFirstName.trim() ||
          data.freeMonths !== 2 || data.trialDays !== 14) return null;
      const referral = { code, referrerFirstName: data.referrerFirstName.trim(), savedAt: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(referral));
      const identify = new Identify();
      identify.set("referral_code", code);
      identify.set("referred", true);
      amplitude.identify(identify);
      return referral;
    } catch {
      clearReferral();
      return null;
    } finally {
      window.clearTimeout(timeout);
    }
  })();
  return landingLookup;
}

const ReferralContext = createContext<{ referral: Referral | null; pending: boolean }>({ referral: null, pending: false });

export function ReferralProvider({ children }: PropsWithChildren) {
  const [lookup] = useState(validateLanding);
  const [state, setState] = useState(() => {
    const pending = isReferralLink(window.location.search);
    return { referral: pending ? null : getStoredReferral(), pending };
  });

  useEffect(() => {
    let active = true;
    lookup.then((referral) => {
      if (active) setState({ referral, pending: false });
    });
    return () => { active = false; };
  }, [lookup]);

  useEffect(() => {
    if (state.pending) return;
    const refresh = () => setState({ referral: getStoredReferral(), pending: false });
    const remaining = state.referral ? state.referral.savedAt + MAX_AGE - Date.now() : 0;
    // setTimeout is limited to a signed 32-bit delay (less than 30 days).
    const timeout = remaining > 0 ? window.setTimeout(refresh, Math.min(remaining, 2147483647)) : undefined;
    window.addEventListener("storage", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [state]);

  return <ReferralContext.Provider value={state}>{children}</ReferralContext.Provider>;
}

export const useReferral = () => useContext(ReferralContext);
